import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Database, FileText, Plus, Trash2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useOrganization } from "@/App";
import { providerCredentialsService } from "@/services/providerCredentialsService";
import type { KnowledgeProvider, KnowledgeQdrantProvider } from "@/types/knowledgeProviders";
import QdrantProviderCard from "./QdrantProviderCard";

interface OrgKeys {
  qdrant: boolean;
  voyage: boolean;
}

export default function KnowledgeProvidersPanel() {
  const { currentOrganization } = useOrganization();
  const form = useFormContext();
  const providers: KnowledgeProvider[] = form.watch("knowledgeBaseProviders") ?? [];
  const [keys, setKeys] = useState<OrgKeys | null>(null);

  useEffect(() => {
    if (!currentOrganization?.id) return;
    providerCredentialsService
      .getProviderCredentials(currentOrganization.id)
      .then((rows) => {
        const active = (name: string) => rows.some((r) => r.provider_name === name && r.is_active);
        setKeys({ qdrant: active("qdrant"), voyage: active("voyage") });
      })
      .catch(() => setKeys({ qdrant: false, voyage: false }));
  }, [currentOrganization?.id]);

  const setProviders = (next: KnowledgeProvider[]) =>
    form.setValue("knowledgeBaseProviders", next, { shouldDirty: true });

  const update = (id: string, patch: Partial<KnowledgeProvider>) =>
    setProviders(providers.map((p) => (p.id === id ? ({ ...p, ...patch } as KnowledgeProvider) : p)));

  const hasBuiltin = providers.some((p) => p.type === "builtin");
  const hasQdrant = providers.some((p) => p.type === "qdrant");

  const add = (type: "builtin" | "qdrant") => {
    const base = { id: crypto.randomUUID(), enabled: true };
    setProviders([
      ...providers,
      type === "builtin" ? { ...base, type } : { ...base, type, collections: [] },
    ]);
  };

  // Tool names must be unique per assistant, across every provider.
  const allToolNames = providers.flatMap((p) =>
    p.type === "qdrant" ? p.collections.map((c) => c.tool_name) : [],
  );

  const missing = keys && (!keys.qdrant || !keys.voyage)
    ? [!keys.qdrant && "Qdrant", !keys.voyage && "Voyage"].filter(Boolean).join(" and ")
    : null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Providers</CardTitle>
          <CardDescription>
            Sources the assistant can search. Each enabled Qdrant collection becomes one search tool.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {missing && (
            <Alert>
              <AlertDescription>
                No active {missing} provider in this organization. Add {missing === "Qdrant and Voyage" ? "them" : "it"} under{" "}
                <a href="/providers" target="_blank" rel="noreferrer" className="underline">
                  Organization &rarr; Providers
                </a>{" "}
                to search a Qdrant collection.
              </AlertDescription>
            </Alert>
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" disabled={hasBuiltin} onClick={() => add("builtin")}>
              <Plus className="h-4 w-4 mr-1" /> Voxi documents
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={hasQdrant} onClick={() => add("qdrant")}>
              <Plus className="h-4 w-4 mr-1" /> Qdrant
            </Button>
          </div>
          {providers.length === 0 && (
            <p className="text-sm text-muted-foreground">No providers added yet.</p>
          )}
        </CardContent>
      </Card>

      {providers.map((provider) => (
        <Card key={provider.id}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-2">
              {provider.type === "builtin" ? <FileText className="h-5 w-5" /> : <Database className="h-5 w-5" />}
              <CardTitle className="text-base">
                {provider.type === "builtin" ? "Voxi documents" : "Qdrant"}
              </CardTitle>
              {!provider.enabled && <Badge variant="secondary">Off</Badge>}
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={provider.enabled} onCheckedChange={(enabled) => update(provider.id, { enabled })} />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setProviders(providers.filter((p) => p.id !== provider.id))}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {provider.type === "builtin" ? (
              <p className="text-sm text-muted-foreground">
                Searches the documents uploaded in the Documents tab.
              </p>
            ) : (
              <QdrantProviderCard
                provider={provider}
                organizationId={currentOrganization?.id}
                keysReady={Boolean(keys?.qdrant && keys?.voyage)}
                allToolNames={allToolNames}
                onChange={(next: KnowledgeQdrantProvider) => update(provider.id, next)}
              />
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
